import React, { useState, useEffect, useCallback } from 'react';
import { useRecovery } from '../context/RecoveryContext';
import { CheckCircle, Lock, Play, Coffee, Pause, RotateCcw, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const RecoveryFocusView: React.FC = () => {
    const { activePlan, completeTask, exitRecovery } = useRecovery();

    // Timer state
    const [timeLeft, setTimeLeft] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);

    // Get current task
    const currentTask = activePlan?.tasks[activePlan.currentStepIndex];
    const isBreak = currentTask?.recoveryAction === 'break';
    const progress = activePlan ? (activePlan.currentStepIndex / activePlan.tasks.length) * 100 : 0;

    // Reset timer when task changes
    useEffect(() => {
        if (currentTask) {
            setTimeLeft(currentTask.recommendedDuration * 60); // Convert minutes to seconds
            setIsRunning(false);
            setHasStarted(false);
        }
    }, [currentTask?.id]);

    // Timer countdown
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        if (isRunning && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0 && hasStarted) {
            setIsRunning(false);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isRunning, timeLeft, hasStarted]);

    const startTimer = () => {
        setIsRunning(true);
        setHasStarted(true);
    };

    const pauseTimer = () => {
        setIsRunning(false);
    };

    const resetTimer = () => {
        if (currentTask) {
            setTimeLeft(currentTask.recommendedDuration * 60);
            setIsRunning(false);
            setHasStarted(false);
        }
    };

    // Format time as MM:SS
    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    if (!activePlan || !currentTask) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 flex flex-col">
            {/* Header */}
            <header className="h-16 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-6 bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm">
                        Recovery Mode
                    </span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                        Step {activePlan.currentStepIndex + 1} of {activePlan.tasks.length}
                    </span>
                </div>
                <button
                    onClick={exitRecovery}
                    className="text-sm text-slate-500 hover:text-red-600 font-medium transition-colors"
                >
                    Exit Recovery
                </button>
            </header>

            {/* Main Focus Area */}
            <main className="flex-1 overflow-hidden flex flex-col lg:flex-row">

                {/* Left: The Linear Timeline */}
                <aside className="hidden lg:flex flex-col w-72 xl:w-80 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-y-auto">
                    <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                        <h3 className="font-bold text-slate-900 dark:text-white text-lg">Your Survival Plan</h3>
                        <p className="text-xs text-slate-500 mt-1">Focus on one task at a time</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                        {activePlan.tasks.map((task, idx) => {
                            const isActive = idx === activePlan.currentStepIndex;
                            const isDone = idx < activePlan.currentStepIndex;
                            const isLockedItem = idx > activePlan.currentStepIndex;
                            const isBreakItem = task.recoveryAction === 'break';

                            return (
                                <div
                                    key={task.id}
                                    className={`p-3 rounded-xl border text-sm transition-all ${isActive
                                        ? 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800 shadow-sm'
                                        : isDone
                                            ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800/50'
                                            : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 opacity-60'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-green-500' :
                                                isActive ? (isBreakItem ? 'bg-green-500' : 'bg-red-500') :
                                                    'bg-slate-300 dark:bg-slate-600'
                                            }`}>
                                            {isDone && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                                            {isActive && (isBreakItem ? <Coffee className="w-3.5 h-3.5 text-white" /> : <Play className="w-3.5 h-3.5 text-white" />)}
                                            {isLockedItem && <Lock className="w-3 h-3 text-white" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <span className={`font-medium block truncate ${isDone ? 'line-through text-slate-400' :
                                                    isActive ? 'text-slate-900 dark:text-white' :
                                                        'text-slate-500'
                                                }`}>
                                                {task.title}
                                            </span>
                                            {isActive && (
                                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                                    {task.recommendedDuration} min
                                                </span>
                                            )}
                                        </div>
                                        {isActive && <ChevronRight size={16} className="text-red-500" />}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </aside>

                {/* Center: The ACTIVE Task */}
                <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentTask.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="max-w-lg w-full text-center"
                        >
                            {/* Icon */}
                            <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6 shadow-lg ${isBreak
                                    ? 'bg-gradient-to-br from-green-400 to-emerald-500'
                                    : 'bg-gradient-to-br from-red-500 to-orange-500'
                                }`}>
                                {isBreak ? <Coffee size={36} className="text-white" /> : <Play size={36} className="text-white ml-1" />}
                            </div>

                            {/* Task Title */}
                            <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-2">
                                {currentTask.title}
                            </h1>

                            {currentTask.description && (
                                <p className="text-base text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                                    {currentTask.description}
                                </p>
                            )}

                            {/* Timer Card */}
                            <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 mb-8 shadow-xl border border-slate-200 dark:border-slate-700">
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
                                    {timeLeft === 0 && hasStarted ? 'Time\'s Up!' : isRunning ? 'Time Remaining' : 'Recommended Time'}
                                </p>
                                <p className={`text-6xl lg:text-7xl font-mono font-bold mb-6 ${timeLeft === 0 && hasStarted
                                        ? 'text-green-500'
                                        : timeLeft < 60 && isRunning
                                            ? 'text-red-500 animate-pulse'
                                            : 'text-slate-900 dark:text-white'
                                    }`}>
                                    {formatTime(timeLeft)}
                                </p>

                                {/* Timer Controls */}
                                <div className="flex items-center justify-center gap-3">
                                    {!isRunning ? (
                                        <button
                                            onClick={startTimer}
                                            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold shadow-md transition-all hover:scale-105 active:scale-95 ${isBreak
                                                    ? 'bg-green-500 hover:bg-green-600 text-white'
                                                    : 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white'
                                                }`}
                                        >
                                            <Play size={18} />
                                            {hasStarted ? 'Resume' : 'Start Timer'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={pauseTimer}
                                            className="flex items-center gap-2 px-6 py-3 rounded-full font-semibold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-white hover:bg-slate-300 dark:hover:bg-slate-600 shadow-md transition-all"
                                        >
                                            <Pause size={18} />
                                            Pause
                                        </button>
                                    )}
                                    {hasStarted && (
                                        <button
                                            onClick={resetTimer}
                                            className="p-3 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                            title="Reset Timer"
                                        >
                                            <RotateCcw size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Complete Task Button */}
                            <button
                                onClick={() => completeTask(currentTask.id)}
                                className={`px-8 py-4 rounded-full text-lg font-bold shadow-lg transform transition-all hover:scale-105 active:scale-95 ${isBreak
                                        ? 'bg-green-500 hover:bg-green-600 text-white shadow-green-200'
                                        : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100'
                                    }`}
                            >
                                {isBreak ? "Break Over → Next Task" : "✓ Mark as Done"}
                            </button>

                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>

            {/* Progress Footer */}
            <div className="h-2 bg-slate-200 dark:bg-slate-800">
                <motion.div
                    className="h-full bg-gradient-to-r from-red-500 to-orange-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5 }}
                />
            </div>
        </div>
    );
};
